import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import React from "react";
import { BsInfoCircleFill } from "react-icons/bs";
import { MdCheck, MdFileCopy, MdHighlightOff } from "react-icons/md";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverArrow,
} from "@chakra-ui/react";

const AdvancedInfoModal = ({
  info,
  varient,
  client_is_deleted,
}: {
  info?: any;
  varient?: string;
  client_is_deleted?: boolean;
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [copyPayment, setCopyPayment] = React.useState<boolean>(false);
  const [copyRefund, setCopyRefund] = React.useState<boolean>(false);
  const [copyGateway, setCopyGateway] = React.useState<boolean>(false);
  const [activeTab, setActiveTab] = React.useState<string>("payment");
  const [isCloseModal, setIsCloseModal] = React.useState<boolean>(false);

  const handleClose = () => {
    setIsCloseModal(true);
    setTimeout(() => {
      setIsCloseModal(false);
      onClose();
    }, 320);
  };

  const handleOpen = () => {
    setActiveTab("payment");
    onOpen();
  };

  const copyToClipboard = (data: any, setter: (v: boolean) => void) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setter(true);
    setTimeout(() => setter(false), 1500);
  };

  return (
    <>
      {/* Trigger */}
      <Popover trigger="hover" placement="bottom">
        <PopoverTrigger>
          <button
            className="aim-trigger"
            onClick={handleOpen}
            disabled={!info}
            aria-label="View transaction info"
          >
            <BsInfoCircleFill
              className="aim-trigger-icon--sm"
            />
          </button>
        </PopoverTrigger>
        <PopoverContent
          border="1px solid #bfdbfe"
          borderRadius="12px"
          boxShadow="0 8px 24px rgba(15,23,42,0.08)"
          bg="#eff6ff"
          maxW="220px"
          p="0"
          overflow="hidden"
          zIndex="20"
        >
          <PopoverHeader
            bg="#dbeafe"
            fontSize="12px"
            fontWeight="700"
            color="#1e40af"
            borderBottom="1px solid #bfdbfe"
            px="14px"
            py="10px"
          >
            View Transaction Info
          </PopoverHeader>
          <PopoverArrow bg="#eff6ff" />
        </PopoverContent>
      </Popover>

      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

        <div
          className={`aim-panel${
            isOpen && !isCloseModal ? " aim-panel--open" : " aim-panel--close"
          }`}
        >
          {/* Top glow bar */}
          <div className="aim-top-glow" />

          {/* Header */}
          <div className="aim-header">
            <div className="aim-header-brand">
              <div className="aim-header-logo">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <circle cx="12" cy="16" r="0.5" fill="#fff" />
                </svg>
              </div>
              <div>
                <h2 className="aim-header-title">Payment Info</h2>
                <p className="aim-header-sub">Transaction &amp; gateway response details</p>
              </div>
            </div>
            <button
              className="aim-close-btn"
              onClick={handleClose}
              aria-label="Close"
            >
              <MdHighlightOff size={22} />
            </button>
          </div>

          {/* Tab bar */}
          <div className="aim-tab-bar">
            <button
              className={`aim-tab${activeTab === "payment" ? " aim-tab--active" : ""}`}
              onClick={() => setActiveTab("payment")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="1" y="4" width="22" height="16" rx="2" />
                <line x1="1" y1="10" x2="23" y2="10" />
              </svg>
              Payment Response
            </button>
            <button
              className={`aim-tab${activeTab === "gateway" ? " aim-tab--active" : ""}`}
              onClick={() => setActiveTab("gateway")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
              Gateway Response
            </button>
          </div>

          {/* Body */}
          <div className="aim-body">
            {activeTab === "payment" && (
              <div className="aim-content-area">
                {/* Payment Response */}
                <div className="aim-json-section">
                  <div className="aim-json-header">
                    <div className="aim-json-label">
                      <div className="aim-json-dot aim-json-dot--blue" />
                      Payment Response
                    </div>
                    <button
                      className={`aim-copy-btn${copyPayment ? " aim-copy-btn--copied" : ""}`}
                      onClick={() => copyToClipboard(info?.meta_info, setCopyPayment)}
                    >
                      {copyPayment ? (
                        <>
                          <MdCheck size={14} />
                          Copied!
                        </>
                      ) : (
                        <>
                          <MdFileCopy size={14} />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <div className="aim-json-wrap">
                    <pre className="aim-json-pre">
                      {JSON.stringify(info?.meta_info, null, 2)}
                    </pre>
                  </div>
                </div>

                {/* Refund Info (if present) */}
                {info?.refund_info && (
                  <div className="aim-json-section">
                    <div className="aim-json-header">
                      <div className="aim-json-label">
                        <div className="aim-json-dot aim-json-dot--amber" />
                        Refund Info
                      </div>
                      <button
                        className={`aim-copy-btn${copyRefund ? " aim-copy-btn--copied" : ""}`}
                        onClick={() => copyToClipboard(info?.refund_info, setCopyRefund)}
                      >
                        {copyRefund ? (
                          <>
                            <MdCheck size={14} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <MdFileCopy size={14} />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                    <div className="aim-json-wrap">
                      <pre className="aim-json-pre">
                        {JSON.stringify(info?.refund_info, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "gateway" && (
              <div className="aim-content-area">
                <div className="aim-json-section">
                  <div className="aim-json-header">
                    <div className="aim-json-label">
                      <div className="aim-json-dot aim-json-dot--green" />
                      Gateway Response
                    </div>
                    <button
                      className={`aim-copy-btn${copyGateway ? " aim-copy-btn--copied" : ""}`}
                      onClick={() => copyToClipboard(info?.gateway_response, setCopyGateway)}
                    >
                      {copyGateway ? (
                        <>
                          <MdCheck size={14} />
                          Copied!
                        </>
                      ) : (
                        <>
                          <MdFileCopy size={14} />
                          Copy
                        </>
                      )}
                    </button>
                  </div>
                  <div className="aim-json-wrap">
                    <pre className="aim-json-pre">
                      {JSON.stringify(info?.gateway_response, null, 2)}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="aim-footer">
            <div className="aim-footer-note">
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              Read-only transaction data
            </div>
            <button className="aim-btn-close" onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        /* ── Trigger button ── */
        .aim-trigger {
          display: inline-flex; align-items: center; justify-content: center;
          width: 32px; height: 32px; border-radius: 8px;
          background: #eff6ff; border: 1.5px solid #bfdbfe;
          color: #2563eb; cursor: pointer;
          transition: all .15s;
        }
        .aim-trigger:hover { background: #dbeafe; border-color: #93c5fd; }
        .aim-trigger:disabled { opacity: .45; cursor: not-allowed; }
        .aim-trigger-icon { width: 16px; height: 16px; }
        .aim-trigger-icon--sm { width: 14px; height: 14px; }

        /* ── Slide panel ── */
        .aim-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 640px;
          background: #ffffff;
          z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -12px 0 60px rgba(15,23,42,.18);
          transition: transform .35s cubic-bezier(0.16,1,0.3,1);
          overflow: hidden;
        }
        .aim-panel--open  { transform: translateX(0); }
        .aim-panel--close { transform: translateX(100%); }

        /* Top glow */
        .aim-top-glow {
          height: 3px;
          background: linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744);
          background-size: 200% 100%;
          animation: aimGlow 3s linear infinite;
          flex-shrink: 0;
        }
        @keyframes aimGlow { 0%{background-position:0%} 100%{background-position:200%} }

        /* Header */
        .aim-header {
          padding: 18px 26px;
          background: linear-gradient(135deg, #0f2744 0%, #1a3d6b 60%, #1d4ed8 100%);
          display: flex; align-items: center; justify-content: space-between;
          flex-shrink: 0;
        }
        .aim-header-brand { display: flex; align-items: center; gap: 13px; }
        .aim-header-logo {
          width: 42px; height: 42px; border-radius: 12px;
          background: rgba(255,255,255,.15);
          border: 1.5px solid rgba(255,255,255,.25);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .aim-header-title {
          font-size: 16px; font-weight: 800; color: #fff;
          letter-spacing: -.3px; margin-bottom: 2px; line-height: 1;
        }
        .aim-header-sub { font-size: 12px; color: rgba(255,255,255,.55); font-weight: 500; }
        .aim-close-btn {
          background: none; border: none; cursor: pointer;
          color: rgba(255,255,255,.65);
          display: flex; align-items: center; padding: 2px;
          transition: color .15s;
        }
        .aim-close-btn:hover { color: #fff; }

        /* Tab bar */
        .aim-tab-bar {
          display: flex; gap: 0;
          border-bottom: 1.5px solid #e2e8f0;
          background: #fafbfc;
          flex-shrink: 0;
          padding: 0 24px;
        }
        .aim-tab {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #64748b;
          padding: 14px 16px; border: none; background: none;
          border-bottom: 2.5px solid transparent;
          cursor: pointer; transition: all .18s;
          margin-bottom: -1.5px;
        }
        .aim-tab:hover { color: #1d4ed8; }
        .aim-tab--active {
          color: #1d4ed8;
          border-bottom-color: #2563eb;
          font-weight: 700;
        }

        /* Body */
        .aim-body {
          flex: 1; overflow-y: auto; overflow-x: hidden;
          background: #f8fafc;
        }
        .aim-body::-webkit-scrollbar { width: 4px; }
        .aim-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        .aim-content-area {
          padding: 20px 24px;
          display: flex; flex-direction: column; gap: 20px;
        }

        /* JSON sections */
        .aim-json-section {
          background: #fff;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          overflow: hidden;
        }
        .aim-json-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 12px 16px;
          background: #f8fafc;
          border-bottom: 1px solid #f1f5f9;
        }
        .aim-json-label {
          display: flex; align-items: center; gap: 8px;
          font-size: 12.5px; font-weight: 700; color: #374151;
          text-transform: uppercase; letter-spacing: .4px;
        }
        .aim-json-dot {
          width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
        }
        .aim-json-dot--blue  { background: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.15); }
        .aim-json-dot--amber { background: #d97706; box-shadow: 0 0 0 3px rgba(217,119,6,.15); }
        .aim-json-dot--green { background: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,.15); }

        .aim-copy-btn {
          display: inline-flex; align-items: center; gap: 5px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 12px; font-weight: 600; color: #64748b;
          padding: 5px 12px; border-radius: 7px;
          border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .aim-copy-btn:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }
        .aim-copy-btn--copied {
          color: #15803d; border-color: #bbf7d0; background: #f0fdf4;
        }

        .aim-json-wrap {
          overflow: auto;
          max-height: 420px;
        }
        .aim-json-wrap::-webkit-scrollbar { width: 4px; height: 4px; }
        .aim-json-wrap::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        .aim-json-pre {
          margin: 0;
          padding: 16px 18px;
          font-family: 'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Courier New', monospace;
          font-size: 12.5px;
          line-height: 1.65;
          color: #1e293b;
          white-space: pre;
          word-break: normal;
          background: #fff;
          tab-size: 2;
        }

        /* Footer */
        .aim-footer {
          padding: 14px 26px;
          border-top: 1.5px solid #f1f5f9;
          background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-shrink: 0;
        }
        .aim-footer-note {
          display: flex; align-items: center; gap: 6px;
          font-size: 12px; color: #94a3b8; font-weight: 500;
        }
        .aim-btn-close {
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13.5px; font-weight: 600; color: #64748b;
          padding: 10px 24px; border-radius: 10px;
          border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .aim-btn-close:hover { background: #f1f5f9; border-color: #cbd5e1; color: #374151; }

        @media (max-width: 520px) {
          .aim-panel { max-width: 100%; }
          .aim-header { padding: 16px 18px; }
          .aim-content-area { padding: 16px; }
          .aim-tab-bar { padding: 0 16px; }
          .aim-footer { padding: 12px 18px; }
        }
      `}</style>
    </>
  );
};

export default AdvancedInfoModal;